import { eSubscriptionStatus, ePlanCode } from './Subscription.enums';
import { eSeatQuoteUnavailable, type tSeatQuote } from './useSeatPurchase.types';

const SEATS_PRODUCT_TYPE = 'SEATS';

/**
 * Stripe's value for invoice terms.
 *
 * Lives here, beside the seat quote, so the provider layer owns it: the payment-method
 * hook needs it too, and importing it from a Team-tab modal folder pointed the
 * dependency the wrong way — a provider reaching into a page's internals.
 */
export const COLLECTION_METHOD_SEND_INVOICE = 'send_invoice';

/**
 * Subscription states that may buy a seat.
 *
 * Deliberately the subscription's OWN status, not `workspaceInfo.isActive`: that flag
 * is workspace-enabled state, and gating money on it already sent a churned customer
 * (active workspace, no live subscription) into a canceled Stripe subscription — see
 * `docs/solutions/logic-errors/router-gates-on-workspace-flag-not-subscription-status.md`.
 * `PAST_DUE` and `NO_PAID` are rejected by both the create and upgrade guards upstream,
 * so they belong to the pay-balance flow rather than here.
 */
export const PURCHASE_ELIGIBLE_STATUSES: readonly eSubscriptionStatus[] = [
  eSubscriptionStatus.ACTIVE,
  eSubscriptionStatus.TRIAL,
  eSubscriptionStatus.SCHEDULED_CANCELLATION,
];

type tSeatQuoteInput = {
  status?: eSubscriptionStatus;
  planCode?: ePlanCode;
  currency?: string;
  purchasedSeatsCost?: number;
  /**
   * The catalogue. `allRelatedProducts` is authoritative when the two lists diverge:
   * `available_for_purchase` DROPS a product once `max_quantity` is reached (default
   * 1), so reading that one first makes the quote vanish after the first seat and can
   * resolve a different product than the purchase buys.
   */
  allRelatedProducts?: Array<{ uuid: string; productType: string; price: number }>;
  /** Consulted only when the authoritative list carries no SEATS row. */
  availableForPurchase?: Array<{ uuid: string; productType: string; price: number }>;
  /** True when the catalogue could not be read at all. */
  hasProductsError?: boolean;
};

/**
 * What one seat costs, or an explicit reason there is no price.
 *
 * The single resolution path behind every seat price rendered in Settings — the Team
 * tab's add-teammate flow and the Subscription tab's ManageUsers modal both land here,
 * which is what makes "the two surfaces cannot quote different numbers" structural
 * rather than a convention.
 *
 * It replaces two zero-returning fallbacks. A `$0` seat reads as a free seat, and the
 * two states it used to collapse — "this plan sells no seats" and "we could not read
 * the catalogue" — need different things said to the customer.
 */
export function resolveSeatQuote(input: tSeatQuoteInput): tSeatQuote {
  const { status, planCode, currency = '', purchasedSeatsCost } = input;

  if (!status || !PURCHASE_ELIGIBLE_STATUSES.includes(status)) {
    return {
      available: false,
      reason: eSeatQuoteUnavailable.SUBSCRIPTION_NOT_ELIGIBLE,
    };
  }

  // A legacy plan carries no SEATS product; the seat it already pays for is the price.
  // Carried over from `getExtraSeatsPrice` rather than dropped.
  if (planCode === ePlanCode.PLAN_LEGACY) {
    if (!purchasedSeatsCost) {
      return { available: false, reason: eSeatQuoteUnavailable.NO_SEATS_PRODUCT };
    }
    return {
      available: true,
      recurringAmountCents: purchasedSeatsCost,
      currency,
      // No SEATS product exists on a legacy plan, so there is nothing to buy here.
      // The price still renders; the flow routes to the Subscription tab.
      productId: '',
      purchasable: false,
    };
  }

  if (input.hasProductsError) {
    return { available: false, reason: eSeatQuoteUnavailable.PRODUCTS_UNAVAILABLE };
  }

  const findSeats = (
    list?: Array<{ uuid: string; productType: string; price: number }>,
  ) =>
    list?.find(
      (product) =>
        product.productType?.toLowerCase() === SEATS_PRODUCT_TYPE.toLowerCase(),
    );

  // Authoritative list first (Q2), then the filtered one — so a payload that only
  // populates `available_for_purchase` still quotes, exactly as it did before U1.
  const seatsProduct =
    findSeats(input.allRelatedProducts) ?? findSeats(input.availableForPurchase);

  if (!seatsProduct?.price) {
    return { available: false, reason: eSeatQuoteUnavailable.NO_SEATS_PRODUCT };
  }

  return {
    available: true,
    recurringAmountCents: seatsProduct.price,
    currency,
    productId: seatsProduct.uuid,
    purchasable: true,
  };
}

/**
 * The recurring seat price in cents, or `null` when there is none.
 *
 * For callers that only need the number and already handle absence. `null` is the
 * point: it cannot be formatted into "$0.00" by accident.
 */
export function resolveSeatPriceCents(input: tSeatQuoteInput): number | null {
  const quote = resolveSeatQuote(input);
  return quote.available ? quote.recurringAmountCents : null;
}
